import type { ReactNode } from 'react';
import {
  Archive,
  ArrowLeft,
  BriefcaseBusiness,
  ArrowLeftRight,
  BadgeDollarSign,
  BarChart3,
  Bot,
  Building2,
  Cable,
  CalendarClock,
  CalendarDays,
  ChartPie,
  CheckSquare,
  ClipboardCheck,
  ClipboardList,
  ClipboardPen,
  Columns3,
  Contact,
  Crosshair,
  FileSignature,
  FileText,
  Files,
  Gauge,
  Globe,
  Handshake,
  Headset,
  Hourglass,
  Inbox,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  ListChecks,
  ListOrdered,
  Mail,
  MapPin,
  Medal,
  Megaphone,
  MessageCircle,
  MessageSquareText,
  MessagesSquare,
  Newspaper,
  Package,
  Plug,
  PlugZap,
  Receipt,
  RotateCcw,
  ScrollText,
  Settings,
  Settings2,
  ShieldCheck,
  ShoppingCart,
  Shuffle,
  SlidersHorizontal,
  SquareKanban,
  Sun,
  Tags,
  Target,
  TrendingUp,
  Trophy,
  Truck,
  UserPlus,
  Users,
  UsersRound,
  Webhook,
  Workflow,
} from 'lucide-react';
import type { NavItem } from '@castinflow/ui';
import type { PlanModule } from '@castinflow/shared';
import { tr } from '../i18n/tr';
import { settingsNavText } from '../framework/settings/settings-nav-strings';
import { NAV_ITEM_SECTION } from './shell-settings';
import { integrationsText } from '../framework/integrations/integrations-strings';
import { planText } from '../framework/plan/plan-strings';
import { quarantineText } from '../framework/quarantine/quarantine-strings';
import { adsText } from '../framework/ads/ads-strings';
import { potentialText } from '../framework/potential/potential-strings';
import { LIFECYCLE_ROUTES, lifecycleNav } from '../framework/lifecycle/lifecycle-nav';
import { acquisitionNav } from '../framework/acquisition/acquisition-strings';
import { marketingText } from '../framework/marketing/marketing-strings';
import { audienceText } from '../framework/marketing/audience-strings';
import { deliveryNavText } from '../framework/delivery/delivery-nav-strings';
import { navText } from './navigation-strings';

/**
 * Ana menü tanımı (tek kaynak) — v3.9 iki seviyeli, alan odaklı navigasyon (PM "radikal navigasyon sadeleştirmesi",
 * 14 Ekim 2026). Her ekranın TEK kanonik konumu vardır: yedi ana alan (Bugün, CRM, Satış, İletişim, Pazarlama, İşler,
 * Raporlar) ve ayrı giriş olarak Yönetim (beş kategori). Rota, yetki ve test kimlikleri v3.7 menüsüyle aynıdır; yalnız
 * yerleştirme değişti. Menüde olmayan rotalar (ayrıntı, sihirbaz, geri dönüş sayfaları) önek tablosuyla alana bağlanır.
 *
 * Görünürlük YALNIZ kullanılabilirlik içindir; yetki sınırı API'dedir (WP 2.7 K-7).
 * `requires`: öğeyi görmek için gereken yeteneklerden en az biri (sunucunun `me.capabilities` listesi).
 * `module` (v4.5 A-0): öğe yalnız modül `read_only` ya da `active` iken çizilir; modül durumu bilinmiyorken (yanıt beklenirken,
 * alınamadıysa, eski sunucu) çizilmez — modülü olmayan kiracı yeni menü öğesi görmez. Karar yine sunucudadır.
 */
export interface NavItemDef {
  id: string;
  label: string;
  icon: ReactNode;
  href?: string;
  status?: 'available' | 'soon';
  requires?: readonly string[];
  testId?: string;
  module?: PlanModule;
}

export type MainAreaId =
  'bugun' | 'crm' | 'satis' | 'iletisim' | 'pazarlama' | 'isler' | 'raporlar';
export type AreaId = MainAreaId | 'yonetim';
/** Ana alanların sabit sırası (menü, mobil çekmece ve Görünüm ayarı aynı sırayı gösterir) */
export const MAIN_AREA_IDS: readonly MainAreaId[] = [
  'bugun',
  'crm',
  'satis',
  'iletisim',
  'pazarlama',
  'isler',
  'raporlar',
];
export type AdminCategoryId = 'kurulus' | 'kurallar' | 'baglantilar' | 'plan' | 'veri';

export interface AreaSectionDef {
  id: string;
  label?: string;
  items: readonly NavItemDef[];
}
export interface AreaDef {
  id: MainAreaId;
  icon: ReactNode;
  /**
   * Ray düğmesinin açtığı öğe (Z6): alan adıyla aynı adı taşıyan ekran varsa ray oraya gider ("Bugün" → `/today`).
   * Öğe görünmüyorsa (yetki/bölüm kapalı) alanın ilk görünen öğesine düşülür; yoksa her zaman ilk öğe.
   */
  entryItemId?: string;
  sections: readonly AreaSectionDef[];
}
export interface AdminCategoryDef {
  id: AdminCategoryId;
  icon: ReactNode;
  items: readonly NavItemDef[];
}

const ICON = 18;
const READ = ['records.read', 'records.read.own'] as const;
const MANAGE = ['settings.manage'] as const;

export const ADMIN_HUB_HREF = '/yonetim';
export const adminCategoryHref = (id: AdminCategoryId): string => `${ADMIN_HUB_HREF}/${id}`;

export const AREAS: readonly AreaDef[] = [
  {
    id: 'bugun',
    icon: <Sun size={ICON} />,
    // Z6 (PM incelemesi 04.11): ray "Bugün" gerçek Bugün ekranını açar; Genel bakış panelde ayrı öğe, logo ona gider
    entryItemId: 'today',
    sections: [
      {
        id: 'bugun',
        items: [
          {
            id: 'overview',
            label: tr.nav.overview,
            icon: <LayoutDashboard size={ICON} />,
            href: '/',
            testId: 'nav-home',
          },
          {
            // v3 C1: "Bugün" — bekleyen işler kovaları (kayıt okuma yetkisi)
            id: 'today',
            label: tr.nav.today,
            icon: <Sun size={ICON} />,
            href: '/today',
            requires: READ,
            testId: 'nav-today',
          },
          {
            id: 'feed',
            label: tr.nav.feed,
            icon: <Newspaper size={ICON} />,
            href: '/feed',
            requires: READ,
            testId: 'nav-feed',
          },
        ],
      },
    ],
  },
  {
    id: 'crm',
    icon: <Handshake size={ICON} />,
    sections: [
      {
        id: 'crm-leads',
        label: navText.sections.crmLeads,
        items: [
          {
            id: 'leads',
            label: tr.nav.leads,
            icon: <UserPlus size={ICON} />,
            href: '/records/lead',
            requires: READ,
            testId: 'nav-leads',
          },
          {
            id: 'lead-overview',
            label: tr.nav.leadOverview,
            icon: <Gauge size={ICON} />,
            href: '/sales/leads/overview',
            requires: READ,
            testId: 'nav-lead-overview',
          },
          {
            id: 'lead-sequences',
            label: tr.nav.leadSequences,
            icon: <ListOrdered size={ICON} />,
            href: '/sales/leads/sequences',
            requires: READ,
            testId: 'nav-lead-sequences',
          },
          {
            // v3.1 S9: bekleyen dağıtım ve istisnalar (liste sunucuda ACL'li)
            id: 'lead-distribution',
            label: acquisitionNav.distributionQueue,
            icon: <Hourglass size={ICON} />,
            href: '/sales/leads/distribution',
            requires: READ,
            testId: 'nav-lead-distribution',
          },
        ],
      },
      {
        id: 'crm-records',
        label: navText.sections.crmRecords,
        items: [
          {
            id: 'contacts',
            label: tr.nav.contacts,
            icon: <Contact size={ICON} />,
            href: '/records/contact',
            requires: READ,
            testId: 'nav-contacts',
          },
          {
            id: 'companies',
            label: tr.nav.companies,
            icon: <Building2 size={ICON} />,
            href: '/records/company',
            requires: READ,
            testId: 'nav-companies',
          },
          {
            id: 'transfers',
            label: tr.nav.transfers,
            icon: <ArrowLeftRight size={ICON} />,
            href: '/transfers',
            requires: ['records.import', 'records.export'],
            testId: 'nav-transfers',
          },
        ],
      },
    ],
  },
  {
    id: 'satis',
    icon: <TrendingUp size={ICON} />,
    sections: [
      {
        id: 'satis-surec',
        label: navText.sections.salesProcess,
        items: [
          {
            id: 'opportunities',
            label: tr.nav.opportunities,
            icon: <Target size={ICON} />,
            href: '/records/opportunity',
            requires: READ,
            testId: 'nav-opportunities',
          },
          {
            id: 'opportunity-board',
            label: tr.nav.opportunityBoard,
            icon: <Columns3 size={ICON} />,
            href: '/sales/opportunities/board',
            requires: READ,
            testId: 'nav-opportunity-board',
          },
          {
            // v4.6: satış takibi (öncelikli takip, değerlendirme bekleyen adaylar, geciken takipler; listeler sunucuda ACL'li)
            id: 'sales-follow-up',
            label: potentialText.worklist.title,
            icon: <ListChecks size={ICON} />,
            href: '/sales/follow-up',
            requires: READ,
            testId: 'nav-sales-follow-up',
          },
          {
            id: 'approvals',
            label: tr.nav.approvals,
            icon: <ClipboardCheck size={ICON} />,
            href: '/sales/approvals',
            requires: READ,
            testId: 'nav-approvals',
          },
        ],
      },
      {
        id: 'satis-belgeler',
        label: navText.sections.salesDocuments,
        items: [
          {
            id: 'proposals',
            label: tr.nav.proposals,
            icon: <FileText size={ICON} />,
            href: '/records/proposal',
            requires: READ,
            testId: 'nav-proposals',
          },
          {
            id: 'orders',
            label: tr.nav.orders,
            icon: <ShoppingCart size={ICON} />,
            href: '/records/order',
            requires: READ,
            testId: 'nav-orders',
          },
          {
            id: 'order-board',
            label: tr.nav.orderBoard,
            icon: <SquareKanban size={ICON} />,
            href: '/sales/orders/board',
            requires: READ,
            testId: 'nav-order-board',
          },
          {
            id: 'contracts',
            label: tr.nav.contracts,
            icon: <FileSignature size={ICON} />,
            href: '/records/contract',
            requires: READ,
            testId: 'nav-contracts',
          },
          {
            id: 'purchase-orders',
            label: tr.nav.purchaseOrders,
            icon: <Truck size={ICON} />,
            href: '/records/purchase_order',
            requires: READ,
            testId: 'nav-purchase-orders',
          },
          {
            // D1 (TI-022): tedarikçi = ilişki tipi "Tedarikçi" olan şirket/kişi (yeni varlık değil)
            id: 'suppliers',
            label: tr.nav.suppliers,
            icon: <Building2 size={ICON} />,
            href: '/sales/suppliers',
            requires: READ,
            testId: 'nav-suppliers',
          },
        ],
      },
      {
        id: 'satis-katalog',
        label: navText.sections.salesCatalog,
        items: [
          {
            id: 'products',
            label: tr.nav.products,
            icon: <Package size={ICON} />,
            href: '/records/product',
            requires: READ,
            testId: 'nav-products',
          },
        ],
      },
    ],
  },
  {
    id: 'iletisim',
    icon: <MessageCircle size={ICON} />,
    sections: [
      {
        id: 'iletisim',
        items: [
          {
            // v3.1 S10: ekip gelen kutusu (konuşmalar; kayıt okuma yetkisi, erişim konuşma bazında sunucuda)
            id: 'inbox',
            label: tr.nav.inbox,
            icon: <MessagesSquare size={ICON} />,
            href: '/inbox',
            requires: READ,
            testId: 'nav-inbox',
          },
          {
            id: 'tickets',
            label: tr.nav.tickets,
            icon: <LifeBuoy size={ICON} />,
            href: '/records/ticket',
            requires: READ,
            testId: 'nav-tickets',
          },
          {
            id: 'support-overview',
            label: tr.nav.supportOverview,
            icon: <Headset size={ICON} />,
            href: '/support/overview',
            requires: READ,
            testId: 'nav-support-overview',
          },
        ],
      },
    ],
  },
  {
    id: 'pazarlama',
    icon: <Megaphone size={ICON} />,
    sections: [
      {
        id: 'pazarlama',
        items: [
          {
            // Pazarlama çalışma merkezi (24 Ekim 2026): kaynaktan sonraki işe — ölçüler, çalışma kuyruğu, grafikler; aday okuma
            id: 'marketing-workcenter',
            label: marketingText.nav,
            icon: <Crosshair size={ICON} />,
            href: '/marketing',
            requires: READ,
            testId: 'nav-marketing-workcenter',
          },
          {
            // E2 (24 Ekim 2026): segment = kayıtlı aday listesi; toplam ve izin dağılımı listenin kendisi
            id: 'marketing-segments',
            label: audienceText.nav.segments,
            icon: <UsersRound size={ICON} />,
            href: '/marketing/segments',
            requires: READ,
            testId: 'nav-marketing-segments',
          },
          {
            // E2: takip ve yeniden kazanım kuyruğu (izin + temas sınırına saygı; ileti göndermez)
            id: 'marketing-rewin',
            label: audienceText.nav.rewin,
            icon: <RotateCcw size={ICON} />,
            href: '/marketing/rewin',
            requires: READ,
            testId: 'nav-marketing-rewin',
          },
          {
            // E2: kampanya–görüşme–satış kalite analizi + veri güveni (kaynak kesinliği, harcama kapsamı)
            id: 'marketing-quality',
            label: audienceText.nav.quality,
            icon: <Gauge size={ICON} />,
            href: '/marketing/quality',
            requires: READ,
            testId: 'nav-marketing-quality',
          },
          {
            // v3.1 S9: aday olay havuzu (hatalı/başarısız olaylar, yeniden işleme) — aday olaylarını yönetme yetkisi
            id: 'lead-center',
            label: acquisitionNav.leadCenter,
            icon: <Inbox size={ICON} />,
            href: '/sales/leads/center',
            requires: ['lead_events.manage'],
            testId: 'nav-lead-center',
          },
          {
            // v3.1 S9: kaynak/kampanya başına aday → fırsat → satış (sayılar görülebilen kayıtlardır)
            id: 'campaign-report',
            label: acquisitionNav.campaignReport,
            icon: <Megaphone size={ICON} />,
            href: '/sales/leads/campaign-report',
            requires: READ,
            testId: 'nav-campaign-report',
          },
          {
            // v3.1 S11: reklam harcaması (salt okuma; hesap/kampanya/gün, para birimleri ayrı) — ads.read
            id: 'ad-spend',
            label: adsText.nav.spend,
            icon: <BadgeDollarSign size={ICON} />,
            href: '/ads/spend',
            requires: ['ads.read'],
            testId: 'nav-ad-spend',
          },
          {
            // v3.1 S11: reklam değişikliği istekleri (Castinflow reklam hesabına yazmaz; onaylı yürütme sistemine iletilir)
            id: 'ad-requests',
            label: adsText.nav.requests,
            icon: <ClipboardPen size={ICON} />,
            href: '/ads/requests',
            requires: ['ads.read', 'ads.request', 'ads.approve'],
            testId: 'nav-ad-requests',
          },
        ],
      },
    ],
  },
  {
    id: 'isler',
    icon: <ListChecks size={ICON} />,
    sections: [
      {
        id: 'isler-gorev',
        label: navText.sections.workTasks,
        items: [
          {
            id: 'activities',
            label: tr.nav.activities,
            icon: <CheckSquare size={ICON} />,
            href: '/records/activity',
            requires: READ,
            testId: 'nav-activities',
          },
          {
            id: 'activity-board',
            label: tr.nav.activityBoard,
            icon: <Columns3 size={ICON} />,
            href: '/activities/board',
            requires: READ,
            testId: 'nav-activity-board',
          },
          {
            id: 'activity-calendar',
            label: tr.nav.activityCalendar,
            icon: <CalendarDays size={ICON} />,
            href: '/activities/calendar',
            requires: READ,
            testId: 'nav-activity-calendar',
          },
          {
            id: 'activity-map',
            label: tr.nav.activityMap,
            icon: <MapPin size={ICON} />,
            href: '/activities/map',
            requires: READ,
            testId: 'nav-activity-map',
          },
        ],
      },
      {
        id: 'isler-form',
        label: navText.sections.workForms,
        items: [
          {
            // M5 WP 5.1: formlar (galeri + tanımlı formlar; tüm form belgeleri)
            id: 'forms',
            label: tr.nav.forms,
            icon: <ClipboardPen size={ICON} />,
            href: '/forms',
            requires: READ,
            testId: 'nav-forms',
          },
          {
            id: 'form-documents',
            label: tr.nav.formDocuments,
            icon: <Files size={ICON} />,
            href: '/records/form_document',
            requires: READ,
            testId: 'nav-form-documents',
          },
          {
            // M5 WP 5.2: otomasyon kuralları ve çalışma geçmişi — yalnız yönetici (settings.manage)
            id: 'automation',
            label: tr.nav.automation,
            icon: <Workflow size={ICON} />,
            href: '/automation',
            requires: MANAGE,
            testId: 'nav-automation',
          },
        ],
      },
      {
        // v4.5 B-1a/B-9: kabul edilmiş tekliften kurulan proje ve dönemsel hizmetler — yalnız `delivery` modülü açıksa
        id: 'isler-teslim',
        label: navText.sections.workDelivery,
        items: [
          {
            id: 'teslim',
            label: deliveryNavText.nav,
            icon: <BriefcaseBusiness size={ICON} />,
            href: '/teslim',
            requires: READ,
            testId: 'nav-teslim',
            module: 'delivery',
          },
        ],
      },
    ],
  },
  {
    id: 'raporlar',
    icon: <ChartPie size={ICON} />,
    sections: [
      {
        id: 'raporlar',
        items: [
          {
            id: 'dashboard',
            label: tr.nav.dashboard,
            icon: <BarChart3 size={ICON} />,
            href: '/dashboard',
            requires: READ,
            testId: 'nav-dashboard',
          },
          {
            id: 'goals',
            label: tr.nav.goals,
            icon: <Crosshair size={ICON} />,
            href: '/goals',
            requires: READ,
            testId: 'nav-goals',
          },
          {
            id: 'activity-report',
            label: tr.nav.activityReport,
            icon: <ClipboardList size={ICON} />,
            href: '/dashboard/activity-report',
            requires: READ,
            testId: 'nav-activity-report',
          },
          {
            // M5 WP 5.3: puan, sıralama ve rozetler (özellik kapalıysa ekran bunu söyler; puan gösterilmez)
            id: 'gamification',
            label: tr.nav.gamification,
            icon: <Medal size={ICON} />,
            href: '/gamification',
            requires: READ,
            testId: 'nav-gamification',
          },
        ],
      },
    ],
  },
];

export const ADMIN_ICON = <Settings2 size={ICON} />;

/** Yönetim: beş kategori (PM). Kategoriye girince bağlamsal panelde YALNIZ o kategorinin ayarları görünür. */
export const ADMIN_CATEGORIES: readonly AdminCategoryDef[] = [
  {
    id: 'kurulus',
    icon: <UsersRound size={ICON} />,
    items: [
      {
        id: 'users',
        label: tr.nav.users,
        icon: <Users size={ICON} />,
        href: '/org',
        requires: ['users.manage'],
        testId: 'nav-org',
      },
      {
        // v3.1 A1: Şirketim — herkes görür (salt-okunur); kaydetme yalnız settings.manage (sunucu da reddeder)
        id: 'company-settings',
        label: settingsNavText.company,
        icon: <Building2 size={ICON} />,
        href: '/settings/company',
        testId: 'nav-company-settings',
      },
      {
        id: 'work-calendar',
        label: tr.nav.workCalendar,
        icon: <CalendarClock size={ICON} />,
        href: '/settings/work-calendar',
        testId: 'nav-work-calendar',
      },
    ],
  },
  {
    id: 'kurallar',
    icon: <SlidersHorizontal size={ICON} />,
    items: [
      {
        id: 'settings',
        label: tr.nav.settings,
        icon: <Settings size={ICON} />,
        href: '/settings',
        testId: 'nav-settings',
      },
      {
        // M6 WP 6.2: menü (bölüm görünürlüğü), liste varsayılanları, dil paketleri — yalnız yönetici
        id: 'appearance',
        label: settingsNavText.appearance,
        icon: <SlidersHorizontal size={ICON} />,
        href: '/settings/appearance',
        requires: MANAGE,
        testId: 'nav-appearance',
      },
      {
        // v3.9: rota vardı ama hiçbir yerden bağlantısı yoktu. Sayfanın kendi kuralı: kayıt okuyan görür (şablon/blok
        // seçimi sihirbazda gerekir), kaydetme yalnız `settings.manage` (sunucu da reddeder)
        id: 'sales-settings',
        label: navText.admin.salesSettings,
        icon: <Receipt size={ICON} />,
        href: '/sales/settings',
        requires: READ,
        testId: 'nav-sales-settings',
      },
      {
        // v3.9: menüde yoktu; haber akışı sayfasındaki bağlantı gibi yalnız `settings.manage` görür (görünürlük genişlemez)
        id: 'feed-settings',
        label: navText.admin.feedSettings,
        icon: <Newspaper size={ICON} />,
        href: '/settings/feed',
        requires: MANAGE,
        testId: 'nav-feed-settings',
      },
      {
        // v3.1 S9: ağırlıklı ve takvimli aday dağıtım kuralları — yalnız yönetici
        id: 'distribution-rules',
        label: acquisitionNav.distributionRules,
        icon: <Shuffle size={ICON} />,
        href: '/settings/distribution-rules',
        requires: MANAGE,
        testId: 'nav-distribution-rules',
      },
      {
        // v3.1 S9: kampanya sözlüğü, eşlemeler, tıklama kimliği tercihi — yalnız yönetici
        id: 'acquisition',
        label: acquisitionNav.acquisition,
        icon: <Tags size={ICON} />,
        href: '/settings/acquisition',
        requires: MANAGE,
        testId: 'nav-acquisition',
      },
      {
        // M5 WP 5.3: oyunlaştırma — özellik anahtarı, kurallar, oyunlar, rozetler (yalnız yönetici)
        id: 'gamification-settings',
        label: tr.nav.gamificationSettings,
        icon: <Trophy size={ICON} />,
        href: '/settings/gamification',
        requires: MANAGE,
        testId: 'nav-gamification-settings',
      },
    ],
  },
  {
    id: 'baglantilar',
    icon: <Cable size={ICON} />,
    items: [
      {
        // v3.1 S10: gelen kutusu kanalları + e-posta bastırma listesi — yalnız yönetici
        id: 'channels',
        label: tr.nav.channels,
        icon: <MessagesSquare size={ICON} />,
        href: '/settings/channels',
        requires: MANAGE,
        testId: 'nav-channels',
      },
      {
        // v3.1 S10: hazır yanıtlar (kişisel herkes; ekip yanıtını yönetici düzenler)
        id: 'quick-replies',
        label: tr.nav.quickReplies,
        icon: <MessageSquareText size={ICON} />,
        href: '/settings/quick-replies',
        requires: ['records.write', 'records.write.lite'],
        testId: 'nav-quick-replies',
      },
      {
        // M5 WP 5.4: SMTP sunucusu, e-posta şablonları, gönderim kaydı — yalnız yönetici
        id: 'email',
        label: tr.nav.email,
        icon: <Mail size={ICON} />,
        href: '/settings/email',
        requires: MANAGE,
        testId: 'nav-email',
      },
      {
        // M5 WP 5.6: gelen e-posta (IMAP hesapları, karantina) — yalnız yönetici
        id: 'inbound-email',
        label: tr.nav.inboundEmail,
        icon: <Inbox size={ICON} />,
        href: '/settings/inbound-email',
        requires: MANAGE,
        testId: 'nav-inbound-email',
      },
      {
        // v3.1 S7: sağlayıcı bağlantıları (durum, gönderim koruması, bekleyen iletiler) — yalnız yönetici
        id: 'connections',
        label: settingsNavText.connections,
        icon: <PlugZap size={ICON} />,
        href: '/settings/connections',
        requires: MANAGE,
        testId: 'nav-connections',
      },
      {
        // EK1: ajan profilleri, bağlı uygulama kurulumları ve demo sınaması — yalnız yönetici
        id: 'agents',
        label: settingsNavText.agents,
        icon: <Bot size={ICON} />,
        href: '/settings/agents',
        requires: MANAGE,
        testId: 'nav-agents',
      },
      {
        // M6 WP 6.2: entegrasyon kataloğu (çalışan bağlantıların gerçek durumu) — yalnız yönetici
        id: 'integrations',
        label: settingsNavText.integrations,
        icon: <Plug size={ICON} />,
        href: '/settings/integrations',
        requires: MANAGE,
        testId: 'nav-integrations',
      },
      {
        // M5 WP 5.5: web kancaları (abonelik, teslim geçmişi, başarısız teslimler) — yalnız yönetici
        id: 'webhooks',
        label: integrationsText.nav.webhooks,
        icon: <Webhook size={ICON} />,
        href: '/settings/webhooks',
        requires: MANAGE,
        testId: 'nav-webhooks',
      },
      {
        // M3 herkese açık uçlar (aday web formu, teklif müşteri bağlantısı ayarı) — yalnız yönetici
        id: 'public-links',
        label: tr.nav.publicLinks,
        icon: <Globe size={ICON} />,
        href: '/settings/public-links',
        requires: MANAGE,
        testId: 'nav-public-links',
      },
      {
        // v3.1 S9: gelen aday uçları (imzalı web kancası, Google potansiyel müşteri formu) — yalnız yönetici
        id: 'lead-endpoints',
        label: acquisitionNav.endpoints,
        icon: <Webhook size={ICON} />,
        href: '/settings/lead-endpoints',
        requires: MANAGE,
        testId: 'nav-lead-endpoints',
      },
    ],
  },
  {
    id: 'plan',
    icon: <Layers size={ICON} />,
    items: [
      {
        // M6 WP 6.1: plan ve kullanım (tavanlar, güncel kullanım) — yalnız yönetici
        id: 'plan',
        label: planText.nav,
        icon: <Layers size={ICON} />,
        href: '/settings/plan',
        requires: MANAGE,
        testId: 'nav-plan',
      },
    ],
  },
  {
    id: 'veri',
    icon: <ShieldCheck size={ICON} />,
    items: [
      {
        // M6 WP 6.4: saklama süreleri, kişi/aday verisi talepleri, silme defteri, hesap kapatma — yalnız yönetici
        id: 'data-lifecycle',
        label: lifecycleNav.lifecycle,
        icon: <Archive size={ICON} />,
        href: LIFECYCLE_ROUTES.lifecycle,
        requires: MANAGE,
        testId: 'nav-data-lifecycle',
      },
      {
        // M6 WP 6.4: denetim kayıtları (süzgeçli liste + CSV) — yalnız yönetici
        id: 'audit',
        label: lifecycleNav.audit,
        icon: <ScrollText size={ICON} />,
        href: LIFECYCLE_ROUTES.audit,
        requires: MANAGE,
        testId: 'nav-audit',
      },
      {
        // M6 WP 6.4: süreli, salt-okunur destek erişim izni — yalnız yönetici
        id: 'support-access',
        label: lifecycleNav.support,
        icon: <KeyRound size={ICON} />,
        href: LIFECYCLE_ROUTES.support,
        requires: MANAGE,
        testId: 'nav-support-access',
      },
      {
        // M6 WP 6.2: IP izin listesi (kilitlenme korumalı) — yalnız yönetici
        id: 'ip-allowlist',
        label: settingsNavText.ipAllowlist,
        icon: <ShieldCheck size={ICON} />,
        href: '/settings/security',
        requires: MANAGE,
        testId: 'nav-ip-allowlist',
      },
      {
        // M6 WP 6.6: bekleyen dış etkili işler (geri yükleme sonrası karantina incelemesi) — yalnız yönetici
        id: 'quarantined-jobs',
        label: quarantineText.nav,
        icon: <Hourglass size={ICON} />,
        href: '/settings/quarantined-jobs',
        requires: MANAGE,
        testId: 'nav-quarantined-jobs',
      },
    ],
  },
];

/**
 * Menüde öğesi OLMAYAN rotaların alanı (ayrıntı / sihirbaz / geri dönüş sayfaları). Önce menü öğeleri en uzun önekle
 * eşlenir; eşleşme yoksa bu tablodaki en uzun önek kullanılır.
 */
export const ROUTE_AREA_PREFIXES: ReadonlyArray<
  readonly [prefix: string, area: AreaId, category?: AdminCategoryId]
> = [
  ['/sales/leads', 'crm'],
  ['/marketing', 'pazarlama'],
  ['/sales/identity-reviews', 'crm'],
  ['/imports', 'crm'],
  ['/sales/opportunities', 'satis'],
  ['/sales/proposals', 'satis'],
  ['/sales/orders', 'satis'],
  ['/sales/purchase-orders', 'satis'],
  ['/sales/suppliers', 'satis'],
  ['/activities', 'isler'],
  // v4.5: modül kapalıyken menüde öğe yoktur; adresle gelen kullanıcı yine İşler alanında açıklamayı görür
  ['/teslim', 'isler'],
  ['/dashboard', 'raporlar'],
  ['/users', 'yonetim', 'kurulus'],
  ['/settings', 'yonetim'],
  [ADMIN_HUB_HREF, 'yonetim'],
];

/**
 * Kabuk ayarları (M6 WP 6.2; shell/shell-settings.ts): çağıranın menüsünde görünen bölümler. v3.9: ana alanların sırası
 * sabittir (PM modeli); eski "grup sırası" ayarı saklanır ama alan sırasını değiştirmez.
 */
export interface NavShellSettings {
  sections: readonly string[];
  groupOrder?: readonly string[];
}

export interface VisibleItem extends NavItem {
  areaId: AreaId;
  categoryId?: AdminCategoryId;
}
export interface VisibleSection {
  id: string;
  label?: string;
  items: VisibleItem[];
}
export interface VisibleArea {
  id: MainAreaId;
  label: string;
  description: string;
  icon: ReactNode;
  /** Raydaki bağlantının hedefi: alanın ilk görünen sayfası (öngörülebilir) */
  href: string;
  testId: string;
  sections: VisibleSection[];
}
export interface VisibleCategory {
  id: AdminCategoryId;
  label: string;
  description: string;
  icon: ReactNode;
  href: string;
  testId: string;
  items: VisibleItem[];
}
export interface VisibleNav {
  areas: VisibleArea[];
  admin: VisibleCategory[];
  /** Bütün görünen sayfalar (hızlı erişim ve "sayfaya git" araması bu listeden seçer) */
  items: VisibleItem[];
}

/** Modül durumları (`useModuleStates`); verilmeyen ya da `read_only`/`active` olmayan modülün öğesi çizilmez */
export type NavModuleStates = Readonly<Partial<Record<PlanModule, string>>>;

function visibleItem(
  i: NavItemDef,
  caps: ReadonlySet<string>,
  sectionVisible: (id: string) => boolean,
  modules: NavModuleStates,
): boolean {
  if (i.module !== undefined) {
    const state = modules[i.module];
    if (state !== 'read_only' && state !== 'active') return false;
  }
  return (i.requires === undefined || i.requires.some((c) => caps.has(c))) && sectionVisible(i.id);
}

function toNavItem(i: NavItemDef, areaId: AreaId, categoryId?: AdminCategoryId): VisibleItem {
  return {
    id: i.id,
    label: i.label,
    icon: i.icon,
    status: i.status ?? 'available',
    ...(i.href === undefined ? {} : { href: i.href }),
    ...(i.testId === undefined ? {} : { testId: i.testId }),
    areaId,
    ...(categoryId === undefined ? {} : { categoryId }),
  };
}

/**
 * Yetenek süzgeci: öğe `requires` listesinden en az birine sahipse görünür. `shell` verilirse bölüm görünürlüğü de
 * uygulanır (gizlenen bölümün öğesi çizilmez). `modules` (v4.5): modül öğesi yalnız modül `read_only`/`active` iken görünür;
 * verilmezse modül öğeleri çizilmez (kapalı-güvenli). Boş kalan bölüm, alan ve Yönetim kategorisi DÜŞER. Görünürlük YALNIZ
 * menüyü etkiler; kayıt erişimi sunucudadır (WP 2.7 K-7).
 */
export function filterAreas(
  capabilities: readonly string[],
  shell?: NavShellSettings,
  modules: NavModuleStates = {},
): VisibleNav {
  const caps = new Set(capabilities);
  const visible = shell === undefined ? null : new Set(shell.sections);
  const sectionVisible = (itemId: string): boolean => {
    const section = NAV_ITEM_SECTION[itemId];
    return visible === null || section === undefined || visible.has(section);
  };
  const areas: VisibleArea[] = [];
  for (const a of AREAS) {
    const sections = a.sections
      .map((s) => ({
        id: s.id,
        ...(s.label === undefined ? {} : { label: s.label }),
        items: s.items
          .filter((i) => visibleItem(i, caps, sectionVisible, modules))
          .map((i) => toNavItem(i, a.id)),
      }))
      .filter((s) => s.items.length > 0);
    const first = sections[0]?.items[0];
    if (first?.href === undefined) continue;
    const entry =
      a.entryItemId === undefined
        ? undefined
        : sections.flatMap((s) => s.items).find((i) => i.id === a.entryItemId);
    areas.push({
      id: a.id,
      label: navText.areas[a.id].label,
      description: navText.areas[a.id].description,
      icon: a.icon,
      href: entry?.href ?? first.href,
      testId: `nav-area-${a.id}`,
      sections,
    });
  }
  const admin: VisibleCategory[] = [];
  for (const c of ADMIN_CATEGORIES) {
    const items = c.items
      .filter((i) => visibleItem(i, caps, sectionVisible, modules))
      .map((i) => toNavItem(i, 'yonetim', c.id));
    if (items.length === 0) continue;
    admin.push({
      id: c.id,
      label: navText.admin.categories[c.id].label,
      description: navText.admin.categories[c.id].description,
      icon: c.icon,
      href: adminCategoryHref(c.id),
      testId: `nav-admin-cat-${c.id}`,
      items,
    });
  }
  return {
    areas,
    admin,
    items: [
      ...areas.flatMap((a) => a.sections.flatMap((s) => s.items)),
      ...admin.flatMap((c) => c.items),
    ],
  };
}

/** Etkin öğe: kök yalnız tam eşleşmede, diğerleri önek eşleşmesinde. */
export function isNavActive(href: string | undefined, pathname: string): boolean {
  if (href === undefined) return false;
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

export interface NavLocation {
  areaId: AreaId | null;
  categoryId: AdminCategoryId | null;
  /** Yola uyan EN UZUN menü bağlantısı (menüde tek `aria-current` kalır); menüde olmayan sayfada null */
  activeHref: string | null;
}

/**
 * Konum: önce görünen menü öğeleri (en uzun önek), sonra Yönetim merkezi / kategori adresi, sonra önek tablosu. Doğrudan
 * URL ile giriş ve geri/ileri aynı kuralla doğru alanı seçer (konum yalnız adresten türetilir; ayrı durum tutulmaz).
 */
export function resolveNavLocation(nav: VisibleNav, pathname: string): NavLocation {
  let best: VisibleItem | null = null;
  for (const i of nav.items)
    if (
      i.href !== undefined &&
      isNavActive(i.href, pathname) &&
      (best?.href === undefined || i.href.length > best.href.length)
    )
      best = i;
  if (best !== null)
    return {
      areaId: best.areaId,
      categoryId: best.categoryId ?? null,
      activeHref: best.href ?? null,
    };
  if (pathname === ADMIN_HUB_HREF || pathname.startsWith(`${ADMIN_HUB_HREF}/`)) {
    const seg = pathname.slice(ADMIN_HUB_HREF.length + 1).split('/')[0] ?? '';
    const cat = ADMIN_CATEGORIES.find((c) => c.id === seg)?.id ?? null;
    return { areaId: 'yonetim', categoryId: cat, activeHref: null };
  }
  let fallback: (typeof ROUTE_AREA_PREFIXES)[number] | null = null;
  for (const entry of ROUTE_AREA_PREFIXES)
    if (
      isNavActive(entry[0], pathname) &&
      (fallback === null || entry[0].length > fallback[0].length)
    )
      fallback = entry;
  if (fallback !== null)
    return { areaId: fallback[1], categoryId: fallback[2] ?? null, activeHref: null };
  return { areaId: null, categoryId: null, activeHref: null };
}

/** Yönetim merkezi bağlantısı (merkez panelinin başında; aramalı merkez sayfası) */
export const ADMIN_HUB_ITEM: NavItem = {
  id: 'admin-hub',
  label: navText.admin.hubTitle,
  icon: <LayoutGrid size={ICON} />,
  href: ADMIN_HUB_HREF,
  testId: 'nav-admin-hub',
};

/** Yönetim merkezine dönüş bağlantısı (kategori panelinin başında) */
export const ADMIN_BACK_ITEM: NavItem = {
  id: 'admin-hub',
  label: navText.admin.hubBack,
  icon: <ArrowLeft size={ICON} />,
  href: ADMIN_HUB_HREF,
  testId: 'nav-admin-hub',
};
